// Unpublished Work © 2021-2025 Deere & Company.

import t from 'tcomb';
import {Communication} from './communication';
import {CustomerName} from './customer-name';
import {CustomerType, customerTypes} from '../../enum/customer-type';
import {GovernmentTaxId} from './government-tax-id';
import {Memberships} from './membership';
import {FilingDetails} from './filingDetails';
import {Organizations} from './organizations';

export const Customer = t.struct({
    contactId: t.Number,
    entityId: t.Number,
    profileId: t.String,
    jdfCustomerId: t.Number,
    communication: Communication,
    customerName: CustomerName,
    customerType: CustomerType,
    deceasedOrOutOfBusiness: t.Boolean,
    memberships: Memberships,
    organizations: t.list(Organizations),
    governmentTaxIds: t.list(GovernmentTaxId),
    providedPrivacyPolicy: t.maybe(t.Boolean),
    selectedDBS: t.maybe(t.String),
    selectedOrganization: t.maybe(t.String),
    addedDate: t.String,
    jdDealerCode: t.String,
    jdDealerDescription: t.String,
    jdEmployeeDesc: t.String,
    jdEmployeeInd: t.String,
    jdEnterpriseCustomerIndicator: t.Boolean,
    survivorReturnedForInput: t.Boolean,
    birthDate: t.String,
    competitiveDealer: t.String,
    competitiveDealerInd: t.String,
    legalNameVerDate: t.String,
    legalNameVerInd: t.Boolean,
    usCtznInd: t.maybe(t.Boolean),
    ssn: t.maybe(t.String),
    taxId: t.maybe(t.String),
    filingDetails: FilingDetails
}, {
    defaultProps: {
        contactId: 0,
        entityId: 0,
        profileId: '',
        jdfCustomerId: 0,
        communication: new Communication({}),
        customerName: new CustomerName({}),
        customerType: customerTypes.INDIVIDUAL,
        deceasedOrOutOfBusiness: false,
        memberships: new Memberships({}),
        organizations: [],
        governmentTaxIds: [],
        providedPrivacyPolicy: false,
        selectedDBS: '',
        selectedOrganization: '',
        addedDate: '',
        jdDealerCode: '',
        jdDealerDescription: '',
        jdEmployeeDesc: '',
        jdEmployeeInd: '',
        jdEnterpriseCustomerIndicator: false,
        survivorReturnedForInput: false,
        birthDate: '',
        competitiveDealer: '',
        competitiveDealerInd: '',
        legalNameVerDate: '',
        legalNameVerInd: false,
        usCtznInd: null,
        ssn: '',
        taxId: '',
        filingDetails: new FilingDetails({})
    },
    name: 'Customer'
});

export const customerKeys = {
    CUSTOMER_TYPE: 'customerType',
    PRIVACY_POLICY: 'providedPrivacyPolicy'
};

export const CustomerWithContacts = Customer.extend({
    contacts: t.list(Customer)
}, 'CustomerWithContacts');

export const Customers = t.list(Customer, 'Customers');

const showPrivacyBasedOnCountry = (country, policyList) => policyList.includes(country);

const getPrivacyFooterData = (countryCode, policyList) => ({
    countryCode,
    result: showPrivacyBasedOnCountry(countryCode, policyList)
});

Customer.prototype.showPrivacyFooter = function (policyList) {
    const workFax = getPrivacyFooterData(this.communication.workFax.countryCode, policyList);
    const workPhone = getPrivacyFooterData(this.communication.workPhone.countryCode, policyList);
    const homeFax = getPrivacyFooterData(this.communication.homeFax.countryCode, policyList);
    const homePhone = getPrivacyFooterData(this.communication.homePhone.countryCode, policyList);
    const mobilePhone = getPrivacyFooterData(this.communication.mobilePhone.countryCode, policyList);
    const mailingAddress = getPrivacyFooterData(this.communication.mailingAddress.countryCode, policyList);
    const physicalAddress = getPrivacyFooterData(this.communication.physicalAddress.countryCode, policyList);

    const validationFieldsByCustomerType = {
        [customerTypes.INDIVIDUAL]: [
            workFax,
            workPhone,
            homeFax,
            homePhone,
            mobilePhone,
            mailingAddress,
            physicalAddress
        ],
        [customerTypes.BUSINESS]: [
            workFax,
            workPhone,
            mobilePhone,
            mailingAddress,
            physicalAddress
        ],
        [customerTypes.BUSINESS_CONTACT]: [
            workFax,
            workPhone,
            homeFax,
            homePhone,
            mobilePhone
        ]
    };

    return validationFieldsByCustomerType[this.customerType].filter((row) => row.result)[0] || {
        result: false,
        countryCode: ''
    };
};

Customer.prototype.isEmailValidForSave = function () {
    return Boolean(this.communication.email.emailAddress) || this.communication.email.emailNotAvailableCheckSelected;
};

Customer.prototype.isMiddleNameValidForSave = function () {
    return Boolean(this.customerName.middleName) || this.customerName.middleNameNotAvailableCheckSelected;
};

Customer.prototype.getName = function getName() {
    return this.customerType === customerTypes.BUSINESS ?
        this.customerName.businessName1 :
        this.customerName.formattedName;
};

const isCanMarketFalsy = (key, value) => key === 'canMarket' && !value;

const containsFalsyCanMarket = (object) => typeof object === 'object' && object !== null && showOptOut(object); // eslint-disable-line no-use-before-define

export const showOptOut = (customer) => Object.entries(customer)
    .reduce((accumulator, [key, value]) =>
        accumulator ||
        isCanMarketFalsy(key, value) ||
        containsFalsyCanMarket(value), false);
